import { NuxtAxiosInstance } from '@nuxtjs/axios'
import { AxiosRequestConfig } from 'axios'
import { inject } from 'tsyringe'
import { httpToken } from '~/constants/dependency-injection/tokens'
import { containerScoped } from '~/decorators/dependency-container'
import {
  Path,
  Mapping,
  Field,
  ResponseMapping
} from '~/models/dealer/integration/classified/mapping'
import { OutletMeta } from '~/models/dealer/integration/classified/outlet'
import { StringMap } from '~/models/shared/types'
import { invalidBodyError } from '~/services/errors'
import { LegacyUrlService } from '~/services/legacy/url/LegacyUrlService'

@containerScoped()
export default class DealerClassifiedFieldService {
  requestConfig: AxiosRequestConfig

  constructor(
    @inject(httpToken) private http: NuxtAxiosInstance,
    @inject(LegacyUrlService) legacyUrls: LegacyUrlService
  ) {
    this.requestConfig = {
      baseURL: legacyUrls.getClientSideLegacyBackendApiBaseUrl()
    }
  }

  async getPaths(legacyOutletId: string): Promise<[Path[], OutletMeta]> {
    const response = await this.http.get(
      `/account/xml/${legacyOutletId}/fieldmap-analyze/`,
      this.requestConfig
    )
    return [response.data.paths, this.formatOutletMeta(response.data.meta)]
  }

  private formatOutletMeta(meta: any): OutletMeta {
    return {
      // eslint-disable-next-line camelcase
      id: meta?.new_id?.toString(),
      legacyId: meta?.id?.toString(),
      name: meta?.name
    }
  }

  async getMappings(outletId: string): Promise<StringMap<Mapping>> {
    const response = await this.http.get(
      `/api/panel/xml/${outletId}/field-map/`,
      this.requestConfig
    )

    const mappings = response.data.data?.mappings
    if (!mappings) {
      throw invalidBodyError(response.data)
    }

    return new Map(
      mappings
        .filter((m: ResponseMapping) => m.path)
        .map((m: Mapping) => [m.field, m])
    )
  }

  async createOrUpdateMapping(
    outletId: string,
    field: Field,
    path: Path
  ): Promise<Mapping> {
    await this.http.post(
      `/api/panel/xml/${outletId}/field-map/`,
      { field, path },
      this.requestConfig
    )
    return {
      field,
      path
    }
  }

  async deleteMapping(outletId: string, field: Field): Promise<void> {
    await this.http.post(
      `/api/panel/xml/${outletId}/field-map/`,
      { field, path: null },
      this.requestConfig
    )
  }

  getMappingDependencies(field: Field): Set<Field> {
    const deps = new Set<Field>()
    if (![Field.product, Field.lastUpdate].includes(field)) {
      deps.add(Field.product)
    }
    return deps
  }

  private readonly requiredFields = new Set<Field>([
    Field.product,
    Field.uniqueId,
    Field.categoryName,
    Field.title,
    Field.price
  ])

  fieldIsRequired(field: Field): boolean {
    return this.requiredFields.has(field)
  }
}
